import { Disclosure } from '@headlessui/react';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import { HiBars3, HiXMark } from 'react-icons/hi2';
import { Link, Outlet, useMatch, useNavigate } from 'react-router';
import { useCurrentUser } from '@src/hooks/useCurrentUser';
import { useLogOut } from '@src/hooks/useLogOut';
import UserRegisterGate from '@src/pages/auth/components/UserRegisterGate';
import Button from '../Button';
import { EmailSuppressedBar } from '../EmailSuppressedBar/email-suppressed-bar';
import { HolidayBar } from '../HolidayBar/holiday-bar';
import LanguageSwitcher from './LanguageSwitcher';
import UserAvatarDropdownMenu from './UserAvatarDropdownMenu';

function NavBarLink({
  to,
  children,
}: {
  to: string;
  children: string;
}): JSX.Element {
  const match = useMatch({ path: to });
  return (
    <Link
      to={to}
      className={clsx(
        'inline-flex items-center border-b-2 px-1 pt-1 text-sm font-medium',
        match
          ? 'border-blue-500 text-gray-900'
          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
      )}
    >
      {children}
    </Link>
  );
}

function DisclosureNavButton({
  to,
  children,
}: {
  to: string;
  children: string;
}): JSX.Element {
  const match = useMatch({ path: to });
  return (
    <Disclosure.Button
      as={Link}
      to={to}
      className={clsx(
        'block border-l-4 py-2 pl-3 pr-4 text-base font-medium sm:pl-5 sm:pr-6',
        match
          ? 'border-blue-500 bg-blue-50 text-blue-700'
          : 'border-transparent text-gray-500 hover:border-gray-300 hover:bg-gray-50 hover:text-gray-700',
      )}
    >
      {children}
    </Disclosure.Button>
  );
}

function AppLayout(): JSX.Element {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const namePrefix = (user?.name || 'User').charAt(0);
  const { t } = useTranslation();
  const logOut = useLogOut();

  return (
    <div className="flex min-h-full flex-col">
      <HolidayBar />
      <EmailSuppressedBar />
      <Disclosure as="nav" className="bg-white shadow">
        {({ open }) => (
          <>
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <div className="flex h-16 justify-between">
                <div className="flex">
                  <div className="-ml-2 mr-2 flex items-center md:hidden">
                    {/* Mobile menu button */}
                    <Disclosure.Button className="relative inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500">
                      <span className="absolute -inset-0.5" />
                      <span className="sr-only">
                        {t('buttons.open_main_menu', 'Open main menu')}
                      </span>
                      {open ? (
                        <HiXMark className="block h-6 w-6" aria-hidden="true" />
                      ) : (
                        <HiBars3 className="block h-6 w-6" aria-hidden="true" />
                      )}
                    </Disclosure.Button>
                  </div>
                  <div className="flex flex-shrink-0 items-center">
                    <Link to="/">
                      <img
                        className="h-8 w-auto"
                        src="/images/bordr-logo.svg"
                        alt="Bordr Logo"
                      />
                    </Link>
                  </div>
                  <div className="hidden md:ml-6 md:flex md:space-x-8">
                    <NavBarLink to="/">{t('nav.orders', 'Orders')}</NavBarLink>
                    <NavBarLink to="/documents">
                      {t('nav.documents', 'Documents')}
                    </NavBarLink>
                  </div>
                </div>
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Button
                      onClick={() => navigate('/orders/nif/place-order')}
                      secondary
                    >
                      {t('buttons.order_nif', 'Order a NIF')}
                    </Button>
                  </div>
                  <div className="hidden md:ml-4 md:flex md:flex-shrink-0 md:items-center">
                    <div>
                      <UserAvatarDropdownMenu />
                    </div>
                    <div>
                      <LanguageSwitcher />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <Disclosure.Panel className="sm:hidden">
              <div className="space-y-1 pb-3 pt-2">
                <DisclosureNavButton to="/">
                  {t('nav.orders', 'Orders')}
                </DisclosureNavButton>
                <DisclosureNavButton to="/documents">
                  {t('nav.documents', 'Documents')}
                </DisclosureNavButton>
              </div>
              <div className="border-t border-gray-200 pb-3 pt-4">
                <div className="flex items-center px-4">
                  <div className="flex-shrink-0">
                    <div className="h-8 w-8 rounded-full bg-yellow-400 text-center font-bold leading-8 text-white">
                      {namePrefix}
                    </div>
                  </div>
                  <div className="ml-3">
                    <div className="text-base font-medium text-gray-800">
                      {user?.name}
                    </div>
                    <div className="text-sm font-medium text-gray-500">
                      {user?.email}
                    </div>
                  </div>
                </div>
                <div className="mt-3 space-y-1">
                  <Disclosure.Button
                    as="button"
                    onClick={() => logOut()}
                    className="block px-4 py-2 text-base font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-800"
                  >
                    {t('buttons.log_out', 'Log out')}
                  </Disclosure.Button>
                </div>
              </div>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>

      <div className="flex flex-1 py-10">
        <UserRegisterGate>
          <Outlet />
        </UserRegisterGate>
      </div>
    </div>
  );
}

export default AppLayout;
